import React from 'react';
import { Copy, Eye, ArrowRight, Brain, Target } from 'lucide-react';
import { getCategoryByType } from '../data/categories';

const EssaySampleCard = ({ sample, autofillFromSample, generateFlashCardsFromEssay, startQuizFromEssay }) => {
  const getCategoryColor = (category, type) => {
    // First try to get color from the systematic category data
    if (type) {
      const categoryData = getCategoryByType(type);
      if (categoryData) {
        return categoryData.color;
      }
    }
    
    // Fallback to legacy color mapping for existing essays
    const colorMap = {
      'Informal letters/emails': 'bg-green-100 text-green-800',
      'Letters/emails of request': 'bg-blue-100 text-blue-800',
      'Letters/emails of advice': 'bg-purple-100 text-purple-800',
      'Letters/emails of complaint': 'bg-red-100 text-red-800',
      'Responding to letters/emails of complaint': 'bg-orange-100 text-orange-800',
      'Letters/emails to inform': 'bg-indigo-100 text-indigo-800',
      'Letters/emails of invitation': 'bg-pink-100 text-pink-800',
      'Letters/emails of application': 'bg-teal-100 text-teal-800',
      'Letters/emails to the editor': 'bg-gray-100 text-gray-800',
      'Letters/emails of enquiry': 'bg-pink-100 text-pink-800',
      'Letters/emails of promotion': 'bg-emerald-100 text-emerald-800',
      'Notices': 'bg-cyan-100 text-cyan-800',
      'Proposals': 'bg-violet-100 text-violet-800'
    };
    return colorMap[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryIcon = (type) => {
    if (type) {
      const categoryData = getCategoryByType(type);
      if (categoryData) {
        return categoryData.icon;
      }
    }
    return '📝';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border hover:shadow-md transition-all duration-200 group overflow-hidden">
      {/* Card Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
              {sample.title}
            </h3>
            <span className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${getCategoryColor(sample.category, sample.type)}`}>
              <span>{getCategoryIcon(sample.type)}</span>
              {sample.category}
            </span>
          </div>
          <button
            onClick={() => autofillFromSample(sample)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium group/button"
            title="Use this template in the wizard"
          >
            <Copy className="w-4 h-4" />
            <span>Use Template</span>
            <ArrowRight className="w-4 h-4 group-hover/button:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-6 space-y-4">
        {/* Letter Details */}
        <div className="bg-gray-50 p-4 rounded-lg text-sm">
          <div className="grid grid-cols-2 gap-2 text-gray-600">
            <div><strong className="text-gray-900">To:</strong> {sample.content.recipient}</div>
            <div><strong className="text-gray-900">From:</strong> {sample.content.sender}</div>
            <div><strong className="text-gray-900">Date:</strong> {sample.content.date}</div>
            <div className="col-span-2"><strong className="text-gray-900">Subject:</strong> {sample.content.subject}</div>
          </div>
        </div>

        {/* Content Preview */}
        <div className="space-y-3">
          <div className="border-l-4 border-blue-500 pl-3">
            <strong className="text-blue-600 text-sm">Greeting:</strong>
            <p className="text-gray-700 text-sm mt-1">{sample.content.greeting}</p>
          </div>
          
          <div className="border-l-4 border-green-500 pl-3">
            <strong className="text-green-600 text-sm">Opening:</strong>
            <p className="text-gray-700 text-sm mt-1 line-clamp-2">
              {sample.content.opening.length > 100 
                ? `${sample.content.opening.substring(0, 100)}...` 
                : sample.content.opening
              }
            </p>
          </div>
          
          <div className="border-l-4 border-purple-500 pl-3">
            <strong className="text-purple-600 text-sm">Main Content:</strong>
            <p className="text-gray-700 text-sm mt-1 line-clamp-3">
              {sample.content.mainBody.length > 150 
                ? `${sample.content.mainBody.substring(0, 150)}...` 
                : sample.content.mainBody
              }
            </p>
          </div>
        </div>

        {/* Tips and Structure Hints */}
        {sample.tips && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <h4 className="text-sm font-medium text-yellow-800 mb-2">Writing Tips:</h4>
            <ul className="text-xs text-yellow-700 space-y-1">
              {sample.tips.slice(0, 2).map((tip, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block w-1 h-1 rounded-full bg-yellow-600 mt-2 mr-2 flex-shrink-0"></span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="px-6 py-4 border-t border-gray-100">
        <div className="flex gap-2">
          <button 
            onClick={() => autofillFromSample(sample)}
            className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors group"
          >
            <span>Use Template</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
          {generateFlashCardsFromEssay && (
            <button 
              onClick={() => generateFlashCardsFromEssay(sample)}
              className="flex items-center justify-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              title="Generate Flash Cards from this essay"
            >
              <Brain className="w-4 h-4" />
            </button>
          )}
          {startQuizFromEssay && (
            <button 
              onClick={() => startQuizFromEssay(sample)}
              className="flex items-center justify-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              title="Take Quiz for this essay"
            >
              <Target className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Card Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span className="font-medium">Template ID: {sample.id}</span>
          <div className="flex items-center space-x-1">
            <Eye className="w-4 h-4" />
            <span>Ready to use</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EssaySampleCard;
